/**
 * LiveTravelFeasibility — feature-flagged container that fetches
 * a feasibility result from `/api/scheduling/travel-feasibility`
 * and renders the appropriate Travel-Aware UI.
 *
 * Phase B2 (Feb 18, 2026). Feature flag: per-instructor opt-in
 * via `travel_aware_v1_enabled` on the scheduling-prefs row.
 *
 * Doctrine refs:
 *   /app/memory/TRAVEL_AWARE_SCHEDULING_PROPOSAL.md §4 §8.5
 *   /app/memory/TRAVEL_AWARE_WIREFRAME_PASS.md §2 §3
 *
 * Render rules:
 *   • Flag off → renders nothing (calm-baseline silence).
 *   • API error or missing data → TravelSummaryStrip in
 *     `postcodeMissing` fallback mode + NO warning.
 *   • pressure_band = "comfortable" → ONLY TravelSummaryStrip.
 *   • pressure_band = "tight" | "impossible" | "unknown" →
 *     TravelBufferWarning above the strip.
 *
 * Authority preserved:
 *   • The "Continue anyway" button on TravelBufferWarning is
 *     always enabled (the component owns its own override state).
 *   • This container NEVER hard-blocks a booking flow. The
 *     parent (booking form) decides whether to act on
 *     `feasible` — the container only describes truth.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import TravelSummaryStrip from "./TravelSummaryStrip";
import TravelBufferWarning from "./TravelBufferWarning";
import useTravelAwareFlag from "../../hooks/useTravelAwareFlag";

const API = process.env.REACT_APP_BACKEND_URL;

function gapDisplay(mins) {
  if (mins === null || mins === undefined) return "";
  const h = Math.floor(Math.abs(mins) / 60);
  const m = Math.abs(mins) % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

export default function LiveTravelFeasibility({
  fromPostcode,
  toPostcode,
  adjacentEndIso,
  slotStartIso,
  slotDurationMinutes = 60,
  bufferOverride,
  onAdjust,
}) {
  const { enabled, ready } = useTravelAwareFlag();
  const [feasibility, setFeasibility] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (!fromPostcode && !toPostcode) {
      setFeasibility(null);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (fromPostcode)   params.set("from_postcode", fromPostcode);
        if (toPostcode)     params.set("to_postcode",   toPostcode);
        if (adjacentEndIso) params.set("adjacent_end_iso", adjacentEndIso);
        if (slotStartIso)   params.set("slot_start_iso", slotStartIso);
        params.set("slot_duration_minutes", String(slotDurationMinutes));
        if (bufferOverride !== undefined && bufferOverride !== null) {
          params.set("buffer_minutes_override", String(bufferOverride));
        }
        const r = await axios.get(
          `${API}/api/scheduling/travel-feasibility?${params.toString()}`,
          { withCredentials: true },
        );
        if (!alive) return;
        setFeasibility(r.data);
      } catch {
        if (alive) setFeasibility(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [
    enabled, fromPostcode, toPostcode, adjacentEndIso,
    slotStartIso, slotDurationMinutes, bufferOverride,
  ]);

  if (!ready)   return null;
  if (!enabled) return null;

  if (loading || !feasibility) {
    return (
      <div
        data-testid="live-travel-feasibility-loading"
        className="bg-slate-50 border border-slate-200 rounded-md p-3"
      >
        <p className="text-sm text-slate-400">Checking travel…</p>
      </div>
    );
  }

  const band = feasibility.pressure_band;

  return (
    <div data-testid="live-travel-feasibility" className="space-y-3">
      {(band === "tight"
        || band === "impossible"
        || band === "unknown") && (
        <TravelBufferWarning
          travelMinutes={feasibility.travel_minutes ?? 0}
          bufferMinutes={feasibility.buffer_minutes ?? 0}
          availableMinutes={feasibility.available_gap_minutes ?? 0}
          fromLocation={feasibility.from_label || fromPostcode || "previous lesson"}
          toLocation={feasibility.to_label || toPostcode || "this lesson"}
          onAdjust={onAdjust}
        />
      )}
      <TravelSummaryStrip
        travelMinutes={feasibility.travel_minutes ?? 0}
        bufferMinutes={feasibility.buffer_minutes ?? 0}
        distanceMiles={feasibility.distance_miles ?? 0}
        speedMph={feasibility.speed_mph ?? 0}
        overheadMinutes={feasibility.overhead_minutes ?? 0}
        totalMinutes={feasibility.required_gap_minutes ?? 0}
        gapDisplay={gapDisplay(feasibility.available_gap_minutes)}
        postcodeMissing={
          feasibility.distance_miles === null
          || feasibility.distance_miles === undefined
        }
      />
    </div>
  );
}
